import { useState } from "react";
import {
  Source,
  SourceOrgCategory,
  SourceType,
} from "../../../generated/ingest-api";
import { useListDocumentsByOrg } from "../../hooks/OrgDocument/useListDocumentsByOrg";
import { useListSources } from "../../hooks/OrgSource/useListSources";
import { usePermissions } from "../../hooks";
import { SourceRow } from "./SourceRow";
import styles from "./SourceTable.module.css";

type SourceTypeFilter = "all" | SourceType;

interface SourceTableProps {
  category: SourceOrgCategory;
  orgId: string;
}

export function SourceTable({ category, orgId }: SourceTableProps) {
  const [typeFilter, setTypeFilter] = useState<SourceTypeFilter>("all");
  const { isAdminOfOrg } = usePermissions();
  const isAdmin = isAdminOfOrg(orgId);

  const { data: sources, isLoading, error } = useListSources(category, orgId);
  const { data: documents } = useListDocumentsByOrg(category, orgId);

  const manualDocumentIdsBySourceId = new Map(
    (documents ?? [])
      .filter((document) => document.source_id)
      .map((document) => [document.source_id as string, document.doc_id])
  );

  const filteredSources = sources?.filter(
    (source: Source) =>
      typeFilter === "all" || source.source_type === typeFilter
  );

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="sourceTypeFilter">Type:</label>
          <select
            id="sourceTypeFilter"
            className={styles.filterSelect}
            value={typeFilter}
            onChange={(e) =>
              setTypeFilter(e.target.value as SourceTypeFilter)
            }
          >
            <option value="all">All</option>
            <option value={SourceType.legislation_nz_pco}>NZ Legislation</option>
            {/* <option value={SourceType.http_etag}>HTTP ETag</option> */}
            <option value={SourceType.manual}>Manual</option>
          </select>
        </div>
      </div>

      {isLoading && (
        <div className={styles.loadingState}>Loading sources...</div>
      )}

      {error && (
        <div className={styles.errorState}>Failed to load sources</div>
      )}

      {!isLoading && !error && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Status</th>
              <th>Last Checked</th>
              <th>Last Changed</th>
              {isAdmin && <th>Actions</th>}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredSources && filteredSources.length > 0 ? (
              filteredSources.map((source: Source, index: number) => (
                <SourceRow
                  key={source.source_id ?? `source-${index}`}
                  source={source}
                  category={category}
                  orgId={orgId}
                  isAdmin={isAdmin}
                  manualDocumentId={
                    source.source_id
                      ? manualDocumentIdsBySourceId.get(source.source_id)
                      : undefined
                  }
                />
              ))
            ) : (
              <tr>
                <td
                  colSpan={isAdmin ? 7 : 6}
                  className={styles.emptyState}
                >
                  No sources found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
